/**
 * Platform-aware key/value storage.
 *
 * - On native (iOS / Android via Capacitor): uses @capacitor/preferences, which
 *   maps to Keychain (iOS) and EncryptedSharedPreferences (Android).
 * - On web: falls back to window.localStorage so the existing CRA dev preview
 *   keeps working without any code changes.
 *
 * All exports return Promises so the API is the same in both environments.
 * Use this for the auth Bearer token and any data we want persisted securely
 * on the device once we're inside the native shell.
 */
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

const isNative = () => {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
};

export const storageGet = async (key) => {
  if (isNative()) {
    const { value } = await Preferences.get({ key });
    return value ?? null;
  }
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

export const storageSet = async (key, value) => {
  if (value == null) {
    return storageRemove(key);
  }
  if (isNative()) {
    await Preferences.set({ key, value });
    return;
  }
  try {
    localStorage.setItem(key, value);
  } catch {
    /* quota / private mode — ignore */
  }
};

export const storageRemove = async (key) => {
  if (isNative()) {
    await Preferences.remove({ key });
    return;
  }
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
};

/**
 * Synchronous read of localStorage — kept for the rare bootstrap path where
 * we cannot await (axios default header set on module load). On native this
 * returns null and the async re-hydrate in <AuthProvider> fills it back in.
 */
export const storageGetSync = (key) => {
  if (isNative()) return null;
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

export { isNative as isNativePlatform };
